import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, Building2, MapPin, Phone, Mail, Globe, Star, Users,
  Briefcase, Home, UtensilsCrossed, Heart, GraduationCap, Church,
  Palette, DollarSign, Scale, Building, Baby, Monitor, Car, Scissors,
  MoreHorizontal, Clock, ThumbsUp, Edit2, Trash2, Loader2, ExternalLink,
  Share2, MessageCircle, Sparkles, Zap, Crown, Globe2, Check, ThumbsDown, 
  BarChart3, Pencil, Image as ImageIcon, Video, Play, X, ChevronLeft, ChevronRight,
  Tag, Percent, CheckCircle
} from "lucide-react";
import { 
  getBusinessListing, updateBusinessListing, deleteBusinessListing,
  addBusinessReview, updateBusinessReview, deleteBusinessReview, 
  toggleBusinessRecommendation, checkBusinessRecommendation,
  getBusinessCategories, resolveImageUrl, getFeaturedTiers, createFeaturedCheckout,
  getFeaturedCheckoutStatus, getListingFeaturedStatus, getRatingBreakdown, toggleReviewHelpful,
  uploadImage, createSupportTicket, submitClaimListing
} from "@/lib/api";
import { toast } from "sonner";
import { SafeHTML } from "@/components/SafeHTML";
import { ShareButton } from "@/components/ShareButton";

import { MembershipBanner, MembershipCTA } from "@/components/MembershipModal";

// Category icons mapping
const CATEGORY_ICONS = {
  "Professional Services": Briefcase,
  "Home Services": Home,
  "Food & Dining": UtensilsCrossed,
  "Health & Wellness": Heart,
  "Education": GraduationCap,
  "Religious Goods": Church,
  "Arts & Crafts": Palette,
  "Financial Services": DollarSign,
  "Legal Services": Scale,
  "Real Estate": Building,
  "Childcare": Baby,
  "Technology": Monitor,
  "Automotive": Car,
  "Beauty & Personal Care": Scissors,
  "Other": MoreHorizontal
};

const CATEGORY_COLORS = {
  "Professional Services": "bg-blue-100 text-blue-700",
  "Home Services": "bg-orange-100 text-orange-700",
  "Food & Dining": "bg-red-100 text-red-700",
  "Health & Wellness": "bg-pink-100 text-pink-700",
  "Education": "bg-indigo-100 text-indigo-700",
  "Religious Goods": "bg-purple-100 text-purple-700",
  "Arts & Crafts": "bg-amber-100 text-amber-700",
  "Financial Services": "bg-emerald-100 text-emerald-700",
  "Legal Services": "bg-slate-100 text-slate-700",
  "Real Estate": "bg-teal-100 text-teal-700",
  "Childcare": "bg-rose-100 text-rose-700",
  "Technology": "bg-cyan-100 text-cyan-700",
  "Automotive": "bg-gray-100 text-gray-700",
  "Beauty & Personal Care": "bg-fuchsia-100 text-fuchsia-700",
  "Other": "bg-stone-100 text-stone-700"
};

function StarRating({ rating, size = "sm", interactive = false, onRate }) {
  const stars = [];
  const starSize = size === "sm" ? "w-4 h-4" : size === "md" ? "w-5 h-5" : "w-6 h-6";
  
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`${starSize} ${i <= rating ? "text-[var(--liturgical-secondary)] fill-[#D4AF37]" : "text-[#E5E0D5]"} ${
          interactive ? "cursor-pointer hover:scale-110 transition-transform" : ""
        }`}
        onClick={() => interactive && onRate && onRate(i)}
      />
    );
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

function ReviewCard({ review, currentUserId, listingId, onUpdate, onDelete, onToggleHelpful }) {
  const [helpfulLoading, setHelpfulLoading] = useState(false);
  const isOwner = review.reviewer_id === currentUserId;
  const isHelpful = review.helpful_by?.includes(currentUserId);
  
  const handleHelpful = async () => {
    setHelpfulLoading(true);
    await onToggleHelpful(review.review_id);
    setHelpfulLoading(false);
  };
  
  return (
    <div className="p-4 bg-[#F9F7F2] rounded-lg">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={review.reviewer?.picture} />
            <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">
              {review.reviewer?.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">
              {review.reviewer?.name || "Anonymous"}
            </p>
            <StarRating rating={review.rating} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6B665E] font-['Nunito']">
            {new Date(review.created_at).toLocaleDateString()}
          </span>
          {isOwner && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onUpdate(review)}
                className="h-7 w-7 p-0 text-[#6B665E] hover:text-[var(--liturgical-primary)]"
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(review.review_id)}
                className="h-7 w-7 p-0 text-[#6B665E] hover:text-red-600"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>
      {review.content && (
        <p className="text-sm text-[#2D2A26] font-['Nunito'] mt-3 leading-relaxed">
          {review.content}
        </p>
      )}
      {/* Review Media */}
      {review.media_url && (
        <div className="mt-3">
          <img 
            src={resolveImageUrl(review.media_url)} 
            alt="Review photo" 
            className="rounded-lg max-h-64 object-cover border border-[#E5E0D5]"
          />
        </div>
      )}
      {/* Helpful Button */}
      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[#E5E0D5]">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleHelpful}
          disabled={helpfulLoading || isOwner}
          className={`h-7 px-2 text-xs font-['Nunito'] ${isHelpful ? 'text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`}
        >
          {helpfulLoading ? (
            <Loader2 className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <ThumbsUp className={`w-3 h-3 mr-1 ${isHelpful ? 'fill-current' : ''}`} />
          )}
          Helpful {review.helpful_count > 0 && `(${review.helpful_count})`}
        </Button>
      </div>
    </div>
  );
}

function RatingBreakdownChart({ breakdown, percentages, total }) {
  const ratings = [5, 4, 3, 2, 1];
  
  return (
    <div className="space-y-2">
      {ratings.map(star => (
        <div key={star} className="flex items-center gap-2">
          <span className="text-xs text-[#6B665E] font-['Nunito'] w-4">{star}</span>
          <Star className="w-3.5 h-3.5 text-[var(--liturgical-secondary)] fill-[#D4AF37]" />
          <div className="flex-1 h-2 bg-[#E5E0D5] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[var(--liturgical-secondary)] rounded-full transition-all"
              style={{ width: `${percentages[star] || 0}%` }}
            />
          </div>
          <span className="text-xs text-[#6B665E] font-['Nunito'] w-8 text-right">
            {breakdown[star] || 0}
          </span>
        </div>
      ))}
    </div>
  );
}

function WriteReviewModal({ isOpen, onClose, onSubmit, isLoading, initialData = null }) {
  const [rating, setRating] = useState(initialData?.rating || 0);
  const [content, setContent] = useState(initialData?.content || "");
  const [mediaUrl, setMediaUrl] = useState(initialData?.media_url || null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    if (initialData) {
      setRating(initialData.rating);
      setContent(initialData.content || "");
      setMediaUrl(initialData.media_url || null);
    } else {
      setRating(0);
      setContent("");
      setMediaUrl(null);
    }
  }, [initialData, isOpen]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadImage(file);
      const url = res.data?.url || res.data?.data?.url;
      if (url) {
        setMediaUrl(url);
        toast.success("Image uploaded");
      }
    } catch (err) {
      toast.error("Failed to upload image");
    }
    setUploading(false);
  };

  const handleSubmit = () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }
    onSubmit({ rating, content, media_url: mediaUrl });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            {initialData ? "Edit Your Review" : "Write a Review"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="text-center py-2">
            <p className="text-sm text-[#6B665E] font-['Nunito'] mb-2">How would you rate this business?</p>
            <StarRating rating={rating} size="lg" interactive onRate={setRating} />
          </div>
          
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">
              Your Review (optional)
            </label>
            <Textarea 
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Share your experience..."
              className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[100px]"
            />
          </div>
          
          {/* Image upload */}
          <div className="space-y-1.5">
            <label className="font-['Nunito'] text-xs text-[#2D2A26] font-medium">
              Add Photo (optional)
            </label>
            {mediaUrl ? (
              <div className="relative inline-block">
                <img src={resolveImageUrl(mediaUrl)} alt="" className="h-24 rounded-md border border-[#E5E0D5]" />
                <button
                  onClick={() => setMediaUrl(null)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-[#E5E0D5] font-['Nunito'] text-sm"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                Upload Photo
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="flex-1 border-[#E5E0D5] font-['Nunito']"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isLoading || uploading}
              className="flex-1 bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Review"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Tier icons
const TIER_ICONS = {
  parish: Church,
  diocese: Building2,
  country: Globe2,
  global: Crown
};

const TIER_COLORS = {
  parish: "border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10",
  diocese: "border-[var(--liturgical-primary)] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)]/10",
  country: "border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)] hover:bg-[var(--liturgical-secondary)]/10",
  global: "border-purple-600 text-purple-600 hover:bg-purple-50"
};

function PromoteListingModal({ isOpen, onClose, listing, onSuccess }) {
  const [tiers, setTiers] = useState({});
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTiers, setLoadingTiers] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadTiers();
    }
  }, [isOpen]);

  const loadTiers = async () => {
    setLoadingTiers(true);
    try {
      const res = await getFeaturedTiers();
      setTiers(res.data.tiers || {});
    } catch (err) {
      toast.error("Failed to load pricing");
    }
    setLoadingTiers(false);
  };

  const handlePromote = async () => {
    if (!selectedTier) {
      toast.error("Please select a promotion tier");
      return;
    }

    setLoading(true);
    try {
      const res = await createFeaturedCheckout({
        listing_id: listing.listing_id,
        tier: selectedTier,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create checkout");
      setLoading(false);
    }
  };

  const tierOrder = ["parish", "diocese", "country", "global"];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[var(--liturgical-secondary)]" />
            Promote Your Business
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-2">
          <p className="text-sm text-[#6B665E] font-['Nunito']">
            Boost visibility for "{listing?.name}" by featuring it in the activity feed.
          </p>

          {loadingTiers ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--liturgical-primary)]" />
            </div>
          ) : (
            <div className="grid gap-3">
              {tierOrder.map(tierId => {
                const tier = tiers[tierId];
                if (!tier) return null;
                
                const TierIcon = TIER_ICONS[tierId];
                const isSelected = selectedTier === tierId;
                
                return (
                  <div
                    key={tierId}
                    onClick={() => setSelectedTier(tierId)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? "border-[var(--liturgical-primary)] bg-[var(--liturgical-primary)]/5" 
                        : "border-[#E5E0D5] hover:border-[var(--liturgical-primary)]/50"
                    }`}
                    data-testid={`tier-${tierId}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isSelected ? "bg-[var(--liturgical-primary)] text-white" : "bg-[#F9F7F2] text-[#6B665E]"
                        }`}>
                          <TierIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#2D2A26] font-['Nunito']">
                            {tier.name}
                          </p>
                          <p className="text-xs text-[#6B665E] font-['Nunito']">
                            {tier.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#2D2A26] font-['Nunito']">
                          ${tier.price.toFixed(2)}
                        </p>
                        <p className="text-xs text-[#6B665E] font-['Nunito']">
                          per week
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="mt-2 pt-2 border-t border-[#E5E0D5]">
                        <p className="text-xs text-[var(--liturgical-primary)] font-['Nunito'] flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Featured for {tier.duration_days} days in {tier.reach === "global" ? "all" : tier.reach} feeds
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="flex-1 border-[#E5E0D5] font-['Nunito']"
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePromote}
              disabled={loading || !selectedTier}
              className="flex-1 bg-[var(--liturgical-secondary)] hover:brightness-90 text-white font-['Nunito']"
              data-testid="checkout-promote-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : selectedTier ? (
                `Pay $${tiers[selectedTier]?.price.toFixed(2)}`
              ) : (
                "Select a Tier"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ClaimListingCard({ listing, user, isPublic }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [formLoadedAt] = useState(() => new Date().toISOString());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Name, email, and message are required");
      return;
    }
    setSubmitting(true);
    try {
      await submitClaimListing({
        listing_id: listing.listing_id,
        listing_name: listing.name,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        message: message.trim(),
        honeypot,
        form_loaded_at: formLoadedAt,
      });
      setSubmitted(true);
      toast.success("Claim request submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <Card className="bg-white border-[#E5E0D5]">
      <CardHeader className="pb-2">
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">
          Claim this Listing
        </CardTitle>
      </CardHeader>
      <CardContent>
        {submitted ? (
          <div className="text-center py-2">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-['Nunito'] text-[#2D2A26] font-semibold">Request Submitted</p>
            <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">A site admin will review your claim and get back to you.</p>
          </div>
        ) : !showForm ? (
          <>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mb-3">
              Is this your business? Claim this listing to manage it, respond to reviews, and update your information.
            </p>
            <Button onClick={() => setShowForm(true)}
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-sm"
              data-testid="claim-listing-btn">
              <Mail className="w-4 h-4 mr-1.5" />Claim this Listing
            </Button>
          </>
        ) : (
          <div className="space-y-3">
            {/* Honeypot - hidden from real users, bots fill it */}
            <input type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)}
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }}
              tabIndex={-1} autoComplete="off" name="website_url" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#2D2A26] font-['Nunito'] mb-1 block">Name *</label>
                <Input value={name} onChange={e => setName(e.target.value)}
                  placeholder="Your full name" className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  data-testid="claim-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#2D2A26] font-['Nunito'] mb-1 block">Email *</label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com" className="border-[#E5E0D5] font-['Nunito'] text-sm"
                  data-testid="claim-email-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#2D2A26] font-['Nunito'] mb-1 block">Phone</label>
              <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="(optional)" className="border-[#E5E0D5] font-['Nunito'] text-sm"
                data-testid="claim-phone-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#2D2A26] font-['Nunito'] mb-1 block">How are you connected to this business? *</label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)}
                placeholder="I am the owner/manager of this business..."
                className="border-[#E5E0D5] font-['Nunito'] text-sm min-h-[80px]"
                data-testid="claim-message-input" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={submitting}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-sm">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                Submit Claim
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}
                className="border-[#E5E0D5] font-['Nunito'] text-sm">Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function BusinessListingDetailPage({ currentUser, isPublic = false }) {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRecommended, setIsRecommended] = useState(false);
  const [recommendLoading, setRecommendLoading] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [featuredStatus, setFeaturedStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [ratingBreakdown, setRatingBreakdown] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  
  // Media lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isOwner = !isPublic && currentUser && listing?.owner_id === currentUser.user_id;

  useEffect(() => {
    loadListing();
    loadFeaturedStatus();
    loadRatingBreakdown();
    if (currentUser) {
      checkRecommendation();
    }
    
    // Check for payment success
    const featuredParam = searchParams.get("featured");
    const sessionId = searchParams.get("session_id");
    
    if (featuredParam === "success" && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (featuredParam === "cancelled") {
      toast.info("Promotion cancelled");
      // Clean up URL
      navigate(`/directory/${listingId}`, { replace: true });
    }
  }, [listingId]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const res = await getFeaturedCheckoutStatus(sessionId);
        
        if (res.data.payment_status === "paid") {
          toast.success("Your listing is now featured!");
          loadFeaturedStatus();
          // Clean up URL
          navigate(`/directory/${listingId}`, { replace: true });
          setCheckingPayment(false);
          return;
        } else if (res.data.status === "expired") {
          toast.error("Payment session expired");
          navigate(`/directory/${listingId}`, { replace: true });
          setCheckingPayment(false);
          return;
        }
        
        // Continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info("Payment is being processed...");
          navigate(`/directory/${listingId}`, { replace: true });
          setCheckingPayment(false);
        }
      } catch (err) {
        toast.error("Failed to verify payment");
        setCheckingPayment(false);
      }
    };
    
    poll();
  };

  const loadFeaturedStatus = async () => {
    try {
      const res = await getListingFeaturedStatus(listingId);
      setFeaturedStatus(res.data);
    } catch (err) {
      console.error("Failed to load featured status:", err);
    }
  };

  const loadRatingBreakdown = async () => {
    try {
      const res = await getRatingBreakdown(listingId);
      setRatingBreakdown(res.data);
    } catch (err) {
      console.error("Failed to load rating breakdown:", err);
    }
  };

  const loadListing = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBusinessListing(listingId);
      setListing(res.data);
    } catch (err) {
      console.error("Failed to load listing:", err);
      setError(err.response?.data?.detail || err.message || "Failed to load listing");
      toast.error("Failed to load listing");
    }
    setLoading(false);
  };

  const checkRecommendation = async () => {
    try {
      const res = await checkBusinessRecommendation(listingId);
      setIsRecommended(res.data.recommended);
    } catch (err) {
      console.error("Failed to check recommendation:", err);
    }
  };

  const handleRecommend = async () => {
    setRecommendLoading(true);
    try {
      const res = await toggleBusinessRecommendation(listingId);
      setIsRecommended(res.data.recommended);
      setListing(prev => ({
        ...prev,
        recommendation_count: prev.recommendation_count + (res.data.recommended ? 1 : -1)
      }));
      toast.success(res.data.recommended ? "Recommendation added!" : "Recommendation removed");
    } catch (err) {
      toast.error("Failed to update recommendation");
    }
    setRecommendLoading(false);
  };

  const handleSubmitReview = async (data) => {
    setReviewLoading(true);
    try {
      if (editingReview) {
        await updateBusinessReview(listingId, editingReview.review_id, data);
        toast.success("Review updated!");
      } else {
        await addBusinessReview(listingId, data);
        toast.success("Review submitted!");
      }
      setReviewModalOpen(false);
      setEditingReview(null);
      loadListing();
      loadRatingBreakdown();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    }
    setReviewLoading(false);
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setReviewModalOpen(true);
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review?")) {
      return;
    }
    try {
      await deleteBusinessReview(listingId, reviewId);
      toast.success("Review deleted");
      loadListing();
      loadRatingBreakdown();
    } catch (err) {
      toast.error("Failed to delete review");
    }
  };

  const handleToggleHelpful = async (reviewId) => {
    try {
      const res = await toggleReviewHelpful(listingId, reviewId);
      setListing(prev => ({
        ...prev,
        reviews: prev.reviews.map(r => 
          r.review_id === reviewId 
            ? { ...r, helpful_count: res.data.helpful_count, helpful_by: res.data.is_helpful 
                ? [...(r.helpful_by || []), currentUser.user_id]
                : (r.helpful_by || []).filter(id => id !== currentUser.user_id)
              }
            : r
        )
      }));
    } catch (err) {
      toast.error("Failed to update helpful status");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this listing? This cannot be undone.")) {
      return;
    }
    setDeleteLoading(true);
    try {
      await deleteBusinessListing(listingId);
      toast.success("Listing deleted");
      navigate("/directory");
    } catch (err) {
      toast.error("Failed to delete listing");
    }
    setDeleteLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F9F7F2] gap-4">
        <p className="text-[#991B1B] font-['Nunito']">Error: {error}</p>
        <Button 
          onClick={() => navigate("/directory")}
          className="bg-[var(--liturgical-primary)] text-white font-['Nunito']"
        >
          Back to Directory
        </Button>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <p className="text-[#6B665E] font-['Nunito']">Listing not found</p>
      </div>
    );
  }

  const CategoryIcon = CATEGORY_ICONS[listing.category] || MoreHorizontal;
  const categoryColor = CATEGORY_COLORS[listing.category] || CATEGORY_COLORS["Other"];
  const hasUserReviewed = listing.reviews?.some(r => r.reviewer_id === currentUser?.user_id);

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="business-detail-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate("/directory")}
          className="mb-6 text-[#6B665E] hover:text-[#2D2A26] font-['Nunito']"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Directory
        </Button>

        {/* Main Content */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="md:col-span-2 space-y-6">
            {/* Header Card */}
            <Card className="bg-white border-[#E5E0D5]">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Photo */}
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-[#F9F7F2] flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {listing.photos && listing.photos.length > 0 ? (
                      <img 
                        src={resolveImageUrl(listing.photos[0])} 
                        alt={listing.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <CategoryIcon className="w-10 h-10 text-[#6B665E]" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h1 className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26]">
                            {listing.name}
                          </h1>
                          {featuredStatus?.is_featured && (
                            <Badge className="bg-gradient-to-r from-[#D4AF37] to-[#B8972F] text-white text-[10px] font-['Nunito'] animate-pulse">
                              <Sparkles className="w-3 h-3 mr-1" />
                              FEATURED
                            </Badge>
                          )}
                        </div>
                        <Badge className={`${categoryColor} text-xs font-['Nunito'] mt-1`}>
                          <CategoryIcon className="w-3 h-3 mr-1" />
                          {listing.category}
                        </Badge>
                        {/* Open/Closed Status */}
                        {listing.open_status && listing.open_status.is_open !== null && (
                          <Badge 
                            className={`text-xs font-['Nunito'] mt-1 ${
                              listing.open_status.is_open 
                                ? 'bg-green-100 text-green-700 border-green-200' 
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                            }`}
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            {listing.open_status.status}
                          </Badge>
                        )}
                      </div>
                      
                      {isOwner && (
                        <div className="flex gap-2">
                          {!featuredStatus?.is_featured && (
                            <Button
                              size="sm"
                              onClick={() => setPromoteModalOpen(true)}
                              className="bg-gradient-to-r from-[#D4AF37] to-[#B8972F] text-white hover:opacity-90"
                              data-testid="promote-btn"
                            >
                              <Sparkles className="w-3.5 h-3.5 mr-1" />
                              Promote
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/directory/${listingId}/edit`)}
                            className="border-[#E5E0D5] text-[#6B665E]"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDelete}
                            disabled={deleteLoading}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            {deleteLoading ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Rating & Recommendations */}
                    <div className="flex items-center gap-4 mt-3 flex-wrap">
                      {listing.review_count > 0 && (
                        <div className="flex items-center gap-1">
                          <StarRating rating={Math.round(listing.average_rating)} />
                          <span className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">
                            {listing.average_rating.toFixed(1)}
                          </span>
                          <span className="text-xs text-[#6B665E] font-['Nunito']">
                            ({listing.review_count} {listing.review_count === 1 ? "review" : "reviews"})
                          </span>
                        </div>
                      )}
                      
                      {listing.recommendation_count > 0 && (
                        <span className="text-xs text-[var(--liturgical-primary)] font-['Nunito'] flex items-center gap-1">
                          <ThumbsUp className="w-3 h-3" />
                          {listing.recommendation_count} {listing.recommendation_count === 1 ? "recommendation" : "recommendations"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                {currentUser && !isOwner && (
                  <div className="flex gap-2 mt-4 pt-4 border-t border-[#E5E0D5]">
                    <Button
                      onClick={handleRecommend}
                      disabled={recommendLoading}
                      variant={isRecommended ? "default" : "outline"}
                      className={`flex-1 font-['Nunito'] ${
                        isRecommended 
                          ? "bg-[var(--liturgical-primary)] hover:brightness-90 text-white" 
                          : "border-[var(--liturgical-primary)] text-[var(--liturgical-primary)]"
                      }`}
                    >
                      {recommendLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <ThumbsUp className={`w-4 h-4 mr-2 ${isRecommended ? "fill-current" : ""}`} />
                      )}
                      {isRecommended ? "Recommended" : "Recommend"}
                    </Button>
                    
                    {!hasUserReviewed && (
                      <Button
                        onClick={() => setReviewModalOpen(true)}
                        variant="outline"
                        className="flex-1 font-['Nunito'] border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)]"
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Write Review
                      </Button>
                    )}
                  </div>
                )}
                
                {/* Share Button - visible to all */}
                <div className={`flex gap-2 ${currentUser && !isOwner ? '' : 'mt-4 pt-4 border-t border-[#E5E0D5]'}`}>
                  <ShareButton
                    title={listing.name}
                    description={`${listing.category} · ${listing.address || listing.service_area || ''}`}
                    url={`/directory/${listing.listing_id}`}
                    image={(listing.photos || [])[0]}
                    type="business"
                    variant="outline"
                    size="default"
                    className="flex-1 font-['Nunito'] border-[#E5E0D5] text-[#6B665E]"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-2">
                <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">
                  About
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SafeHTML 
                  html={listing.description}
                  className="text-sm text-[#2D2A26] font-['Nunito'] leading-relaxed prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0"
                />
                
                {listing.about_business && (
                  <>
                    <Separator className="my-4 bg-[#E5E0D5]" />
                    <div>
                      <h3 className="text-sm font-semibold text-[#2D2A26] font-['Nunito'] mb-2">
                        About Our Business
                      </h3>
                      <SafeHTML html={listing.about_business} className="text-sm text-[#6B665E] font-['Nunito'] leading-relaxed prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0" />
                    </div>
                  </>
                )}
                
                {listing.about_owner && (
                  <>
                    <Separator className="my-4 bg-[#E5E0D5]" />
                    <div>
                      <h3 className="text-sm font-semibold text-[#2D2A26] font-['Nunito'] mb-2">
                        About the Owner
                      </h3>
                      <SafeHTML html={listing.about_owner} className="text-sm text-[#6B665E] font-['Nunito'] leading-relaxed prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0" />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Services Offered */}
            {listing.services_offered && listing.services_offered.length > 0 && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                    <Tag className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Services Offered
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {listing.services_offered.map((service, idx) => (
                      <Badge 
                        key={idx} 
                        className="bg-[#F9F7F2] text-[#2D2A26] font-['Nunito'] text-xs border border-[#E5E0D5]"
                      >
                        <CheckCircle className="w-3 h-3 mr-1 text-green-600" />
                        {service}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Parishioner Discounts */}
            {listing.parishioner_discounts && listing.parishioner_discounts.length > 0 && (
              <Card className="bg-white border-[#E5E0D5] border-l-4 border-l-purple-500">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                    <Percent className="w-5 h-5 text-purple-600" />
                    Parishioner Discounts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {listing.parishioner_discounts.map((discount, idx) => (
                      <div key={idx} className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm text-[#2D2A26] font-['Nunito']">
                              {discount.title}
                            </h4>
                            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
                              {discount.description}
                            </p>
                            {discount.conditions && (
                              <p className="text-xs text-[#6B665E] font-['Nunito'] mt-2 italic">
                                * {discount.conditions}
                              </p>
                            )}
                          </div>
                          {discount.discount_value && (
                            <Badge className="bg-purple-600 text-white font-['Nunito'] text-sm px-3 py-1">
                              {discount.discount_value}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#6B665E] font-['Nunito'] mt-3 flex items-center gap-1">
                    <Church className="w-3.5 h-3.5" />
                    Exclusive discounts for parish members. Mention your parish when contacting.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Media Gallery */}
            {((listing.photos && listing.photos.length > 0) || (listing.videos && listing.videos.length > 0)) && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Photos & Videos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Photos Grid */}
                  {listing.photos && listing.photos.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-[#6B665E] font-['Nunito'] mb-2">
                        Photos ({listing.photos.length})
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {listing.photos.map((photo, idx) => (
                          <div 
                            key={idx} 
                            className="aspect-square rounded-lg overflow-hidden bg-[#F9F7F2] cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => {
                              const items = listing.photos.map(p => ({ type: 'image', url: resolveImageUrl(p) }));
                              setLightboxItems(items);
                              setLightboxIndex(idx);
                              setLightboxOpen(true);
                            }}
                            data-testid={`photo-thumbnail-${idx}`}
                          >
                            <img 
                              src={resolveImageUrl(photo)} 
                              alt={`${listing.name} photo ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Videos with Thumbnails */}
                  {listing.videos && listing.videos.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-[#6B665E] font-['Nunito'] mb-2">
                        Videos ({listing.videos.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {listing.videos.map((video, idx) => {
                          const videoUrl = typeof video === 'string' ? video : video.url;
                          const thumbnail = typeof video === 'object' ? video.thumbnail : null;
                          const videoName = typeof video === 'object' ? video.name : null;
                          const durationFormatted = typeof video === 'object' ? video.duration_formatted : null;
                          
                          return (
                            <div 
                              key={idx} 
                              className="rounded-lg overflow-hidden bg-black group relative cursor-pointer"
                              onClick={() => {
                                const photoCount = listing.photos?.length || 0;
                                const items = [
                                  ...(listing.photos || []).map(p => ({ type: 'image', url: resolveImageUrl(p) })),
                                  ...(listing.videos || []).map(v => {
                                    const vUrl = typeof v === 'string' ? v : v.url;
                                    return { type: 'video', url: resolveImageUrl(vUrl) };
                                  })
                                ];
                                setLightboxItems(items);
                                setLightboxIndex(photoCount + idx);
                                setLightboxOpen(true);
                              }}
                              data-testid={`video-thumbnail-${idx}`}
                            >
                              {thumbnail ? (
                                <div className="relative">
                                  <img 
                                    src={thumbnail} 
                                    alt={videoName || `Video ${idx + 1}`}
                                    className="w-full aspect-video object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                      <svg className="w-6 h-6 text-[var(--liturgical-primary)] ml-1" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z"/>
                                      </svg>
                                    </div>
                                  </div>
                                  {durationFormatted && (
                                    <div className="absolute bottom-2 right-2 bg-black/80 px-1.5 py-0.5 rounded text-xs text-white font-['Nunito'] font-medium">
                                      {durationFormatted}
                                    </div>
                                  )}
                                  {videoName && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2">
                                      <p className="text-xs text-white truncate font-['Nunito']">{videoName}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="relative aspect-video bg-gray-900 flex items-center justify-center">
                                  <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-[var(--liturgical-primary)] ml-1" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z"/>
                                    </svg>
                                  </div>
                                  <p className="absolute bottom-2 left-3 text-xs text-white/70 font-['Nunito']">Video {idx + 1}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Reviews */}
            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">
                    Reviews ({listing.reviews?.length || listing.review_count || 0})
                  </CardTitle>
                  {!isPublic && currentUser && !isOwner && !hasUserReviewed && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditingReview(null);
                        setReviewModalOpen(true);
                      }}
                      className="bg-[var(--liturgical-secondary)] hover:brightness-90 text-white font-['Nunito'] text-xs"
                    >
                      <Star className="w-3.5 h-3.5 mr-1" />
                      Write Review
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Public View - Show summary only */}
                {isPublic && listing.reviews_summary && listing.reviews_summary.review_count > 0 && (
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <p className="text-4xl font-bold text-[#2D2A26] font-['Crimson_Text']">
                        {listing.reviews_summary.average_rating?.toFixed(1) || "0.0"}
                      </p>
                      <StarRating rating={Math.round(listing.reviews_summary.average_rating || 0)} size="md" />
                      <p className="text-sm text-[#6B665E] font-['Nunito'] mt-2">
                        {listing.reviews_summary.review_count} {listing.reviews_summary.review_count === 1 ? 'review' : 'reviews'}
                      </p>
                    </div>
                    <div className="bg-[#F9F7F2] rounded-lg p-4">
                      <p className="text-sm text-[#6B665E] font-['Nunito'] mb-2">
                        {listing.reviews_summary.message}
                      </p>
                      <MembershipCTA context="directory" />
                    </div>
                  </div>
                )}
                
                {isPublic && (!listing.reviews_summary || listing.reviews_summary.review_count === 0) && (
                  <div className="text-center py-8">
                    <Star className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                    <p className="text-sm text-[#6B665E] font-['Nunito']">
                      No reviews yet.
                    </p>
                    <p className="text-xs text-[#6B665E] font-['Nunito'] mt-2">
                      <MembershipCTA context="directory" />
                    </p>
                  </div>
                )}
                
                {/* Authenticated View - Show full reviews */}
                {!isPublic && (
                  <>
                    {/* Rating Breakdown */}
                    {ratingBreakdown && ratingBreakdown.total_reviews > 0 && (
                      <div className="mb-6 p-4 bg-[#F9F7F2] rounded-lg">
                        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                          <div className="text-center">
                            <p className="text-4xl font-bold text-[#2D2A26] font-['Crimson_Text']">
                              {ratingBreakdown.average_rating.toFixed(1)}
                            </p>
                            <StarRating rating={Math.round(ratingBreakdown.average_rating)} size="md" />
                            <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">
                              {ratingBreakdown.total_reviews} {ratingBreakdown.total_reviews === 1 ? 'review' : 'reviews'}
                            </p>
                          </div>
                          <div className="flex-1">
                            <RatingBreakdownChart 
                              breakdown={ratingBreakdown.breakdown}
                              percentages={ratingBreakdown.percentages}
                              total={ratingBreakdown.total_reviews}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {!listing.reviews || listing.reviews.length === 0 ? (
                      <div className="text-center py-8">
                        <Star className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                        <p className="text-sm text-[#6B665E] font-['Nunito']">
                          No reviews yet. Be the first to review!
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {listing.reviews.map(review => (
                          <ReviewCard 
                            key={review.review_id} 
                            review={review}
                            currentUserId={currentUser?.user_id}
                            listingId={listingId}
                            onUpdate={handleEditReview}
                            onDelete={handleDeleteReview}
                            onToggleHelpful={handleToggleHelpful}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Contact & Owner Info */}
          <div className="space-y-6">
            {/* Contact Info */}
            <Card className="bg-white border-[#E5E0D5]">
              <CardHeader className="pb-2">
                <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-[#6B665E]" />
                  <span className="text-[#2D2A26] font-['Nunito']">{listing.service_area}</span>
                </div>
                
                {listing.address && (
                  <div className="flex items-start gap-3 text-sm">
                    <Building2 className="w-4 h-4 text-[#6B665E] mt-0.5" />
                    <span className="text-[#2D2A26] font-['Nunito']">{listing.address}</span>
                  </div>
                )}
                
                {listing.phone && (
                  <a 
                    href={`tel:${listing.phone}`}
                    className="flex items-center gap-3 text-sm text-[var(--liturgical-primary)] hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="font-['Nunito']">{listing.phone}</span>
                  </a>
                )}
                
                {listing.email && (
                  <a 
                    href={`mailto:${listing.email}`}
                    className="flex items-center gap-3 text-sm text-[var(--liturgical-primary)] hover:underline"
                  >
                    <Mail className="w-4 h-4" />
                    <span className="font-['Nunito']">{listing.email}</span>
                  </a>
                )}
                
                {listing.website && (
                  <a 
                    href={listing.website.startsWith('http') ? listing.website : `https://${listing.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm text-[var(--liturgical-primary)] hover:underline"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="font-['Nunito'] truncate">{listing.website}</span>
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Owner Info */}
            {/* Owner Info or Claim Button */}
            {listing.is_unclaimed ? (
              <ClaimListingCard listing={listing} user={currentUser} isPublic={isPublic} />
            ) : listing.owner && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">
                    Listed By
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="flex items-center gap-3 cursor-pointer hover:bg-[#F9F7F2] p-2 -m-2 rounded-lg transition-colors"
                    onClick={() => navigate(`/profile/${listing.owner_id}`)}
                  >
                    <Avatar className="h-12 w-12 border border-[#E5E0D5]">
                      <AvatarImage src={listing.owner.picture} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white font-['Nunito']">
                        {listing.owner.name?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">
                        {listing.owner.name}
                      </p>
                      {listing.owner.parish_name && (
                        <p className="text-xs text-[var(--liturgical-secondary)] font-['Nunito'] flex items-center gap-1">
                          <Church className="w-3 h-3" />
                          {listing.owner.parish_name}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Business Hours (if available) */}
            {listing.hours && Object.values(listing.hours).some(h => h) && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Hours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-sm font-['Nunito']">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                      listing.hours[day] && (
                        <div key={day} className="flex justify-between">
                          <span className="text-[#6B665E] capitalize">{day}</span>
                          <span className="text-[#2D2A26]">{listing.hours[day]}</span>
                        </div>
                      )
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Write Review Modal */}
      <WriteReviewModal
        isOpen={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setEditingReview(null);
        }}
        onSubmit={handleSubmitReview}
        isLoading={reviewLoading}
        initialData={editingReview}
      />
      
      {/* Promote Listing Modal */}
      <PromoteListingModal
        isOpen={promoteModalOpen}
        onClose={() => setPromoteModalOpen(false)}
        listing={listing}
        onSuccess={() => {
          setPromoteModalOpen(false);
          loadFeaturedStatus();
        }}
      />
      
      {/* Media Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="bg-black/95 border-none max-w-[95vw] max-h-[95vh] p-0 sm:rounded-xl overflow-hidden" data-testid="media-lightbox">
          <div className="relative flex items-center justify-center w-full h-[90vh]">
            {/* Close button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              data-testid="lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Counter */}
            {lightboxItems.length > 1 && (
              <div className="absolute top-4 left-4 z-10 bg-black/50 text-white text-sm font-['Nunito'] px-3 py-1 rounded-full">
                {lightboxIndex + 1} / {lightboxItems.length}
              </div>
            )}
            
            {/* Previous button */}
            {lightboxItems.length > 1 && (
              <button
                onClick={() => setLightboxIndex((lightboxIndex - 1 + lightboxItems.length) % lightboxItems.length)}
                className="absolute left-3 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
            
            {/* Media content */}
            {lightboxItems[lightboxIndex] && (
              lightboxItems[lightboxIndex].type === 'video' ? (
                <video
                  key={lightboxItems[lightboxIndex].url}
                  controls
                  autoPlay
                  className="max-w-full max-h-[85vh] object-contain"
                >
                  <source src={lightboxItems[lightboxIndex].url} type="video/mp4" />
                </video>
              ) : (
                <img
                  src={lightboxItems[lightboxIndex].url}
                  alt={`Media ${lightboxIndex + 1}`}
                  className="max-w-full max-h-[85vh] object-contain select-none"
                  draggable={false}
                />
              )
            )}
            
            {/* Next button */}
            {lightboxItems.length > 1 && (
              <button
                onClick={() => setLightboxIndex((lightboxIndex + 1) % lightboxItems.length)}
                className="absolute right-3 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Payment Processing Overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="bg-white p-8 max-w-sm mx-4">
            <div className="text-center">
              <Loader2 className="w-10 h-10 animate-spin text-[var(--liturgical-secondary)] mx-auto mb-4" />
              <p className="font-['Crimson_Text'] text-lg text-[#2D2A26] mb-2">
                Processing Payment
              </p>
              <p className="text-sm text-[#6B665E] font-['Nunito']">
                Please wait while we confirm your payment...
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
